import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { Navigate } from 'react-router-dom'
import { UserProfileHelper, ClinicianService, StudyService } from '@lura/shared'
import type { AssignedPatient, StudyWithAssignment } from '@lura/shared'
import { TopNavigation } from '@/components/TopNavigation'
import { PatientId } from '@/components/PatientId'
import { ColorSquare } from '@/components/ColorSquare'

export function PatientsPage() {
  const { user, signOut } = useAuth()
  const [patients, setPatients] = useState<AssignedPatient[]>([])
  const [clinicianStudies, setClinicianStudies] = useState<
    StudyWithAssignment[]
  >([])
  const [selectedStudyId, setSelectedStudyId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Clinician access check - redirect admin-only users to Clinicians page
  if (!UserProfileHelper.isClinician(user)) {
    return <Navigate to="/clinicians" replace />
  }

  useEffect(() => {
    const fetchStudiesAndPatients = async () => {
      if (!user?.profile?.id) return

      try {
        setLoading(true)
        setError(null)

        // Fetch clinician's studies
        const studies = await StudyService.getClinicianStudies(user.profile.id)
        setClinicianStudies(studies)

        // Fetch all patients for the clinician
        const data = await ClinicianService.getClinicianPatients(
          user.profile.id
        )
        setPatients(data)
      } catch (err) {
        console.error('Error fetching data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }

    fetchStudiesAndPatients()
  }, [user?.profile?.id])

  // Filter patients based on selected study
  const filteredPatients = selectedStudyId
    ? patients.filter(p =>
        p.devices_used.some(d => d.study_id === selectedStudyId)
      )
    : patients

  const selectedStudy = selectedStudyId
    ? clinicianStudies.find(s => s.id === selectedStudyId)
    : null

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Patients"
        onSignOut={signOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          <div className="flex items-start justify-between mb-8">
            <div>
              <h2
                className="text-5xl font-normal text-dark-blue"
                style={{ fontFamily: 'Family' }}
              >
                Patients
              </h2>
              {selectedStudy && (
                <p className="mt-2 text-sm text-gray-600">
                  Filtered by study:{' '}
                  <span className="font-medium">{selectedStudy.title}</span>
                </p>
              )}
            </div>

            {/* Study Filter */}
            {clinicianStudies.length > 0 && (
              <div className="max-w-xs">
                <label
                  htmlFor="study-filter"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Filter by Study
                </label>
                <select
                  id="study-filter"
                  value={selectedStudyId || ''}
                  onChange={e => setSelectedStudyId(e.target.value || null)}
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent bg-white"
                >
                  <option value="">
                    All Studies ({patients.length} patients)
                  </option>
                  {clinicianStudies.map(study => {
                    const patientCount = patients.filter(p =>
                      p.devices_used.some(d => d.study_id === study.id)
                    ).length
                    return (
                      <option key={study.id} value={study.id}>
                        {study.title} ({patientCount}{' '}
                        {patientCount === 1 ? 'patient' : 'patients'})
                      </option>
                    )
                  })}
                </select>
              </div>
            )}
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-6 rounded-3xl bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && filteredPatients.length === 0 && (
            <div className="bg-white rounded-3xl shadow-sm p-12 text-center">
              <svg
                className="mx-auto h-16 w-16 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No patients yet
              </h3>
              <p className="text-sm text-gray-500">
                You have no patients assigned yet.
              </p>
            </div>
          )}

          {/* Patients Table */}
          {!loading && !error && filteredPatients.length > 0 && (
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  My Patients
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Patients assigned to you
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-highlight-green">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                        Patient
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                        Study
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                        Devices
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPatients.map(patient => {
                      // Get unique devices from devices_used, filtered by selected study if applicable
                      const patientDevices = selectedStudyId
                        ? patient.devices_used.filter(
                            d => d.study_id === selectedStudyId
                          )
                        : patient.devices_used

                      return (
                        <tr
                          key={patient.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() =>
                            (window.location.href = `/patient/${patient.id}`)
                          }
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                <PatientId patientId={patient.id} />
                              </div>
                              <div className="text-xs text-gray-500">
                                Last active:{' '}
                                {new Date(
                                  patient.last_activity
                                ).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-2">
                              {patientDevices.map((deviceUsage, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center gap-2 text-sm"
                                >
                                  <ColorSquare
                                    color={deviceUsage.study_color}
                                    size="sm"
                                  />
                                  <span className="font-medium text-gray-900">
                                    {deviceUsage.study_title}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {patientDevices.length > 0 ? (
                                <div className="space-y-1">
                                  {patientDevices.map((deviceUsage, idx) => (
                                    <div key={idx}>
                                      {deviceUsage.device_serial}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <svg
                              className="w-5 h-5 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
